import Vue from "vue";
import helper from "../helper";
import cartByRoomMix from "../shoppingCart/prices/CartByRoom";
import promotionConfigHelper from "../../../helpers/promotionConfigHelper";
import { HasQuotingService } from "../../../helpers/albumContractAdapter";

export default {
    mixins: [],
    data: () => ({}),
    computed: {},
    watch: {},
    methods: {
        /**
         * Gets the normalized album promotion configuration from Sivex data.
         *
         * @param {Array<Object>} sivexPayData Sivex quote responses.
         * @returns {Object} Normalized promotion configuration.
         */
        GetPromotionConfig(sivexPayData = []) {
            return promotionConfigHelper.GetPromotionConfigFromSivexData(
                sivexPayData
            );
        },

        /**
         * Checks whether a promotion configuration has the minimum required values.
         *
         * @param {Object} promotion Normalized promotion configuration.
         * @returns {boolean} True when the promotion can be used.
         */
        HasPromotionConfig(promotion = {}) {
            return promotionConfigHelper.HasPromotionConfig(promotion);
        },

        /**
         * Determines whether the promotion can start for the current paid selection count.
         *
         * @param {Object} params Validation parameters.
         * @param {Object} params.promotion Normalized promotion configuration.
         * @param {number} params.selectedParksLength Number of albums selected by the user.
         * @returns {boolean} True when the promotion can start.
         */
        ShouldStartPromotion({ promotion, selectedParksLength }) {
            return (
                this.HasPromotionConfig(promotion) &&
                Number(selectedParksLength || 0) >= Number(promotion.requiredPurchases || 0)
            );
        },

        /**
         * Resolves the promotion mode for the current selection.
         *
         * @param {Object} params Promotion mode parameters.
         * @param {Object} params.promotion Normalized promotion configuration.
         * @param {number} params.selectedParksLength Number of user-selected paid albums.
         * @param {number} params.availableParksLength Total number of available albums.
         * @returns {"none"|"auto"|"manual"} Promotion mode to apply.
         */
        GetPromotionMode({
            promotion,
            selectedParksLength,
            availableParksLength,
        }) {
            if (
                !this.ShouldStartPromotion({ promotion, selectedParksLength }) ||
                Number(availableParksLength || 0) <= Number(promotion.requiredPurchases || 0)
            ) {
                return "none";
            }

            if (
                promotion.autoUnlockEnabled &&
                Number(availableParksLength || 0) <=
                    Number(promotion.autoUnlockThreshold || 0)
            ) {
                return "auto";
            }

            if (
                Number(availableParksLength || 0) >
                Number(promotion.autoUnlockThreshold || 0)
            ) {
                return "manual";
            }

            return "none";
        },

        /**
         * Checks whether the item is a regular park photo album that participates in the promotion.
         *
         * @param {Object} park Park/photo album to evaluate.
         * @param {Object} options Filtering options.
         * @param {boolean} options.excludeUnlocked Whether already-unlocked albums must be ignored.
         * @returns {boolean} True when the park is a regular photo album.
         */
        IsRegularPhotoPark(park, { excludeUnlocked = false } = {}) {
            return (
                park?.AlbumType === 0 &&
                !park?.Park?.IsHotel &&
                !HasQuotingService(park?.QuotingService) &&
                (!excludeUnlocked || !park?.Unlocked)
            );
        },

        /**
         * Checks whether the item is a hotel photo album.
         *
         * @param {Object} park Park/photo album to evaluate.
         * @param {Object} options Filtering options.
         * @param {boolean} options.excludeUnlocked Whether already-unlocked albums must be ignored.
         * @returns {boolean} True when the park is a hotel photo album.
         */
        IsHotelPhotoPark(park, { excludeUnlocked = false } = {}) {
            return (
                park?.AlbumType === 0 &&
                park?.Park?.IsHotel &&
                !HasQuotingService(park?.QuotingService) &&
                (!excludeUnlocked || !park?.Unlocked)
            );
        },

        /**
         * Gets all regular park albums available in a room reservation.
         *
         * @param {Object} album Parent reservation album.
         * @returns {Array<Object>} Regular albums available for promotion.
         */
        GetAllReservationParks(album) {
            return (album?.park || []).filter((park) =>
                this.IsRegularPhotoPark(park)
            );
        },

        /**
         * Gets all regular park albums still available in a download-code flow.
         *
         * @param {Object} album Parent download-code album.
         * @returns {Array<Object>} Regular albums that can still be added.
         */
        GetAllAvailableDownloadCodeParks(album) {
            return (album?.park || []).filter((park) =>
                this.IsRegularPhotoPark(park, { excludeUnlocked: true })
            );
        },

        /**
         * Gets the regular room-search albums manually selected by the user as paid items.
         *
         * @param {Object} album Parent reservation album.
         * @returns {Array<Object>} Ordered paid albums selected by the user.
         */
        GetSelectedUserParks(album) {
            return this.SortPromotionParks(
                (album?.park || []).filter((park) => {
                    return (
                        park.IsAdded &&
                        park.WasSelectedByUser &&
                        this.IsRegularPhotoPark(park)
                    );
                })
            );
        },

        /**
         * Gets the regular download-code albums manually selected by the user as paid items.
         *
         * @param {Object} album Parent download-code album.
         * @returns {Array<Object>} Ordered paid albums selected by the user.
         */
        GetSelectedUserDownloadCodeParks(album) {
            return this.SortPromotionParks(
                (album?.park || []).filter((park) => {
                    return (
                        park.IsAdded &&
                        park.WasSelectedByUser &&
                        this.IsRegularPhotoPark(park, { excludeUnlocked: true })
                    );
                })
            );
        },

        /**
         * Gets albums that were added by the promotion, either automatically or manually.
         *
         * @param {Object} album Parent album object.
         * @param {Object} options Filtering options.
         * @param {boolean} options.excludeUnlocked Whether already-unlocked albums must be ignored.
         * @returns {Array<Object>} Ordered albums included by promotion.
         */
        GetIncludedPromotionParks(album, { excludeUnlocked = false } = {}) {
            return this.SortPromotionParks(
                (album?.park || []).filter((park) => {
                    return (
                        park.IsAdded &&
                        (park.WasAutoAddedByPromotion || park.WasIncludedByPromotion) &&
                        this.IsRegularPhotoPark(park, { excludeUnlocked })
                    );
                })
            );
        },

        /**
         * Gets all currently added regular room-search albums.
         *
         * @param {Object} album Parent reservation album.
         * @returns {Array<Object>} Ordered added regular albums.
         */
        GetCurrentAddedParks(album) {
            return this.SortPromotionParks(
                (album?.park || []).filter((park) => {
                    return park.IsAdded && this.IsRegularPhotoPark(park);
                })
            );
        },

        /**
         * Gets all currently added regular download-code albums that were not previously unlocked.
         *
         * @param {Object} album Parent download-code album.
         * @returns {Array<Object>} Ordered added regular albums.
         */
        GetCurrentAddedDownloadCodeParks(album) {
            return this.SortPromotionParks(
                (album?.park || []).filter((park) => {
                    return (
                        park.IsAdded &&
                        this.IsRegularPhotoPark(park, { excludeUnlocked: true })
                    );
                })
            );
        },

        /**
         * Gets the hotel albums currently added to a room-search cart item.
         *
         * @param {Object} album Parent reservation album.
         * @returns {Array<Object>} Added hotel albums.
         */
        GetCurrentAddedHotels(album) {
            return (album?.park || []).filter((park) => {
                return park.IsAdded && this.IsHotelPhotoPark(park);
            });
        },

        /**
         * Gets the hotel albums currently added in a download-code flow.
         *
         * @param {Object} album Parent download-code album.
         * @returns {Array<Object>} Added hotel albums that were not previously unlocked.
         */
        GetCurrentAddedDownloadCodeHotels(album) {
            return (album?.park || []).filter((park) => {
                return (
                    park.IsAdded &&
                    this.IsHotelPhotoPark(park, { excludeUnlocked: true })
                );
            });
        },

        /**
         * Calculates the next promotion selection order for the album collection.
         *
         * @param {Object} album Parent album object.
         * @returns {number} Next selection order value.
         */
        GetNextPromotionOrder(album) {
            const orders = (album?.park || [])
                .map((park) => Number(park?.PromotionSelectionOrder || 0))
                .filter((order) => order > 0);

            return orders.length ? Math.max(...orders) + 1 : 1;
        },

        /**
         * Ensures that a park album has a stable promotion selection order.
         *
         * @param {Object} park Park/photo album to update.
         * @param {Object|null} album Parent album used to calculate the next order.
         * @returns {void}
         */
        EnsurePromotionOrder(park, album = null) {
            if (!park?.PromotionSelectionOrder) {
                Vue.set(
                    park,
                    "PromotionSelectionOrder",
                    album ? this.GetNextPromotionOrder(album) : Date.now()
                );
            }
        },

        /**
         * Sorts park albums by their promotion selection order while preserving original order as fallback.
         *
         * @param {Array<Object>} parks Park/photo albums to sort.
         * @returns {Array<Object>} Ordered park/photo albums.
         */
        SortPromotionParks(parks = []) {
            return [...parks]
                .map((park, index) => ({
                    park,
                    index,
                    order: Number(park?.PromotionSelectionOrder || 0),
                }))
                .sort((a, b) => {
                    if (a.order && b.order) {
                        return a.order - b.order;
                    }

                    if (a.order) {
                        return -1;
                    }

                    if (b.order) {
                        return 1;
                    }

                    return a.index - b.index;
                })
                .map(({ park }) => park);
        },

        /**
         * Compares two park lists by Park.Id and position.
         *
         * @param {Array<Object>} firstList First list to compare.
         * @param {Array<Object>} secondList Second list to compare.
         * @returns {boolean} True when both lists contain the same parks in the same order.
         */
        SameParkListById(firstList = [], secondList = []) {
            if (!Array.isArray(firstList) || !Array.isArray(secondList)) {
                return false;
            }

            if (firstList.length !== secondList.length) {
                return false;
            }

            return firstList.every((park, index) => {
                return (
                    Number(park?.Park?.Id) ===
                    Number(secondList[index]?.Park?.Id)
                );
            });
        },

        /**
         * Marks a park album as a paid user selection.
         *
         * @param {Object} park Park/photo album selected by the user.
         * @param {Object|null} album Parent album used to assign selection order.
         * @returns {void}
         */
        MarkParkAsPaidSelection(park, album = null) {
            this.EnsurePromotionOrder(park, album);
            Vue.set(park, "IsAdded", true);
            Vue.set(park, "WasSelectedByUser", true);
            Vue.set(park, "WasAutoAddedByPromotion", false);
            Vue.set(park, "WasIncludedByPromotion", false);
            Vue.set(park, "CanBeIncludedByPromotion", false);
        },

        /**
         * Marks a park album as manually included by the promotion.
         *
         * @param {Object} park Park/photo album included by promotion.
         * @param {Object|null} album Parent album used to assign selection order.
         * @returns {void}
         */
        MarkParkAsIncludedSelection(park, album = null) {
            this.EnsurePromotionOrder(park, album);
            Vue.set(park, "IsAdded", true);
            Vue.set(park, "WasSelectedByUser", false);
            Vue.set(park, "WasAutoAddedByPromotion", false);
            Vue.set(park, "WasIncludedByPromotion", true);
            Vue.set(park, "CanBeIncludedByPromotion", false);
        },

        /**
         * Clears every promotion-related flag from a park album.
         *
         * @param {Object} park Park/photo album to reset.
         * @returns {void}
         */
        ClearPromotionFlags(park) {
            Vue.set(park, "WasSelectedByUser", false);
            Vue.set(park, "WasAutoAddedByPromotion", false);
            Vue.set(park, "WasIncludedByPromotion", false);
            Vue.set(park, "CanBeIncludedByPromotion", false);
            Vue.set(park, "PromotionSelectionOrder", null);
        },

        /**
         * Clears manual inclusion candidates without removing albums from the cart.
         *
         * @param {Object} album Parent album object.
         * @returns {void}
         */
        ClearManualIncludeCandidates(album) {
            (album?.park || []).forEach((park) => {
                if (park.CanBeIncludedByPromotion) {
                    Vue.set(park, "CanBeIncludedByPromotion", false);
                }
            });
        },

        /**
         * Removes every album that was added by the promotion.
         *
         * @param {Object} album Parent album object.
         * @returns {void}
         */
        RemovePromotionIncludedParks(album) {
            (album?.park || []).forEach((park) => {
                if (park.WasAutoAddedByPromotion || park.WasIncludedByPromotion) {
                    Vue.set(park, "IsAdded", false);
                    this.ClearPromotionFlags(park);
                }
            });
        },

        /**
         * Removes included albums from the same commercial block as the removed paid album.
         *
         * @param {Object} params Removal parameters.
         * @param {Object} params.album Parent album object.
         * @param {Object} params.removedPark Paid album removed by the user.
         * @param {number} params.maxAlbumsPerBatch Commercial block size.
         * @param {boolean} params.excludeUnlocked Whether already-unlocked albums must be ignored.
         * @returns {number} Number of included albums removed.
         */
        RemovePromotionIncludedParksFromRemovedParkBlock({
            album,
            removedPark,
            maxAlbumsPerBatch = 0,
            excludeUnlocked = false,
        }) {
            const chunkSize = Number(maxAlbumsPerBatch || 0);

            if (!album || !removedPark || !chunkSize) {
                return 0;
            }

            const addedParks = this.SortPromotionParks(
                (album?.park || []).filter((park) => {
                    return (
                        park.IsAdded &&
                        this.IsRegularPhotoPark(park, { excludeUnlocked })
                    );
                })
            );

            const removedParkId = Number(removedPark?.Park?.Id);
            const removedParkIndex = addedParks.findIndex((park) => {
                return Number(park?.Park?.Id) === removedParkId;
            });

            if (removedParkIndex < 0) {
                return 0;
            }

            const blockStart =
                Math.floor(removedParkIndex / chunkSize) * chunkSize;
            const blockParks = addedParks.slice(blockStart, blockStart + chunkSize);
            const blockHasIncludedParks = blockParks.some((park) => {
                return park.WasAutoAddedByPromotion || park.WasIncludedByPromotion;
            });

            if (!blockHasIncludedParks) {
                return 0;
            }

            let removedIncludedParks = 0;

            blockParks.forEach((park) => {
                const isSameRemovedPark =
                    Number(park?.Park?.Id) === removedParkId;
                const isIncludedPark =
                    park.WasAutoAddedByPromotion || park.WasIncludedByPromotion;

                if (!isSameRemovedPark && isIncludedPark) {
                    Vue.set(park, "IsAdded", false);
                    this.ClearPromotionFlags(park);
                    removedIncludedParks++;
                }
            });

            return removedIncludedParks;
        },

        /**
         * Builds the final display list for automatic promotion mode.
         *
         * @param {Object} params Build parameters.
         * @param {Array<Object>} params.selectedParks User-selected paid albums.
         * @param {Array<Object>} params.allAvailableParks Albums available for the promotion.
         * @param {number} params.maxFreeAlbums Maximum number of free albums allowed.
         * @returns {Array<Object>} Paid albums plus automatically included albums.
         */
        BuildAutoAddedParks({ selectedParks, allAvailableParks, maxFreeAlbums }) {
            const selectedParkIds = selectedParks.map((park) => Number(park.Park.Id));

            const remainingParks = allAvailableParks.filter((park) => {
                return !selectedParkIds.includes(Number(park.Park.Id));
            });

            const freeParksToAdd =
                Number(maxFreeAlbums || 0) > 0
                    ? remainingParks.slice(0, Number(maxFreeAlbums))
                    : remainingParks;

            return [...selectedParks, ...freeParksToAdd];
        },

        /**
         * Marks display albums for automatic promotion mode.
         *
         * @param {Object} params Marking parameters.
         * @param {Array<Object>} params.displayParks Albums shown as added.
         * @param {Array<Object>} params.paidParks Albums that are part of the charge.
         * @param {Object|null} params.album Parent album used to assign selection order.
         * @returns {void}
         */
        MarkAutoPromotionParks({ displayParks, paidParks, album = null }) {
            const paidParkIds = paidParks.map((park) => Number(park.Park.Id));

            displayParks.forEach((park) => {
                this.EnsurePromotionOrder(park, album);
                Vue.set(park, "IsAdded", true);

                const wasSelectedByUser = paidParkIds.includes(Number(park.Park.Id));

                Vue.set(park, "WasSelectedByUser", wasSelectedByUser);
                Vue.set(park, "WasAutoAddedByPromotion", !wasSelectedByUser);
                Vue.set(park, "WasIncludedByPromotion", false);
                Vue.set(park, "CanBeIncludedByPromotion", false);
            });
        },

        /**
         * Calculates the included-album slots unlocked by paid albums in manual mode.
         *
         * @param {Object} params Calculation parameters.
         * @param {Object} params.promotion Normalized promotion configuration.
         * @param {number} params.paidParksLength Number of paid albums.
         * @param {number} params.availableParksLength Total number of available albums.
         * @returns {number} Available included-album slots.
         */
        GetManualPromotionIncludedSlots({
            promotion,
            paidParksLength,
            availableParksLength,
        }) {
            const maxAlbumsPerBatch = Number(promotion?.maxAlbumsPerBatch || 0);
            const requiredPurchases = Number(promotion?.requiredPurchases || 0);
            const maxFreeAlbums = Number(promotion?.maxFreeAlbums || 0);
            const includedSlotsPerBatch = Math.max(
                maxAlbumsPerBatch - requiredPurchases,
                0
            );

            if (!maxAlbumsPerBatch || !requiredPurchases || !includedSlotsPerBatch) {
                return 0;
            }

            const paidBatches = Math.floor(
                Number(paidParksLength || 0) / requiredPurchases
            );
            const slotsUnlockedByPaidAlbums = paidBatches * includedSlotsPerBatch;
            const realAvailableSlots = Math.max(
                Number(availableParksLength || 0) - Number(paidParksLength || 0),
                0
            );
            const configuredFreeLimit = maxFreeAlbums > 0
                ? maxFreeAlbums
                : slotsUnlockedByPaidAlbums;

            return Math.max(
                Math.min(
                    slotsUnlockedByPaidAlbums,
                    realAvailableSlots,
                    configuredFreeLimit
                ),
                0
            );
        },

        /**
         * Gets how many included albums are still pending in manual mode.
         *
         * @param {Object} params Calculation parameters.
         * @param {Object} params.promotion Normalized promotion configuration.
         * @param {Array<Object>} params.paidParks Paid albums.
         * @param {Array<Object>} params.includedParks Already-included albums.
         * @param {number} params.availableParksLength Total number of available albums.
         * @returns {number} Pending included-album quantity.
         */
        GetManualPromotionPendingRemaining({
            promotion,
            paidParks,
            includedParks,
            availableParksLength,
        }) {
            const availableIncludedSlots = this.GetManualPromotionIncludedSlots({
                promotion,
                paidParksLength: paidParks.length,
                availableParksLength,
            });

            return Math.max(
                availableIncludedSlots - includedParks.length,
                0
            );
        },

        /**
         * Marks all albums that can still be manually selected as free options.
         * The pending slots limit how many can be included, not how many options
         * the user can choose from.
         *
         * @param {Object} params Selection parameters.
         * @param {Object} params.album Parent album object.
         * @param {Object} params.promotion Normalized promotion configuration.
         * @param {Array<Object>} params.allAvailableParks Albums available for promotion.
         * @param {Array<Object>} params.paidParks Paid albums.
         * @param {Array<Object>} params.includedParks Already-included albums.
         * @returns {void}
         */
        UpdateManualIncludeCandidates({
            album,
            promotion,
            allAvailableParks,
            paidParks,
            includedParks,
        }) {
            this.ClearManualIncludeCandidates(album);

            const requiredPurchases = Number(promotion?.requiredPurchases || 0);

            if (!requiredPurchases || paidParks.length < requiredPurchases) {
                return;
            }

            const pendingIncludedAlbums = this.GetManualPromotionPendingRemaining({
                promotion,
                paidParks,
                includedParks,
                availableParksLength: allAvailableParks.length,
            });

            if (!pendingIncludedAlbums) {
                return;
            }

            const selectedIds = [...paidParks, ...includedParks].map((park) =>
                Number(park.Park.Id)
            );

            this.SortPromotionParks(allAvailableParks)
                .filter((park) => {
                    const parkId = Number(park.Park.Id);
                    return !park.IsAdded && !selectedIds.includes(parkId);
                })
                .forEach((park) => {
                    Vue.set(park, "CanBeIncludedByPromotion", true);
                });
        },

        /**
         * Checks whether manual promotion mode still has pending included albums.
         *
         * @param {Object} params Promotion state parameters.
         * @returns {boolean} True when there are pending included albums.
         */
        IsPromotionManualPending({ promotion, paidParks, includedParks, availableParksLength }) {
            if (
                this.GetPromotionMode({
                    promotion,
                    selectedParksLength: paidParks.length,
                    availableParksLength,
                }) !== "manual"
            ) {
                return false;
            }

            return this.GetManualPromotionPendingRemaining({
                promotion,
                paidParks,
                includedParks,
                availableParksLength,
            }) > 0;
        },

        /**
         * Gets paid parks from a cart item while preserving backwards compatibility.
         *
         * @param {Object} item Cart item.
         * @returns {Array<Object>} Albums that must be treated as paid.
         */
        GetPaidParksFromCartItem(item) {
            if (Array.isArray(item?.PaidParks) && item.PaidParks.length) {
                return item.PaidParks;
            }

            if (Array.isArray(item?.Parks) && item.Parks.length) {
                const selectedByUser = item.Parks.filter(
                    (park) => park.WasSelectedByUser
                );

                if (selectedByUser.length) {
                    return selectedByUser;
                }
            }

            return Array.isArray(item?.Parks) ? item.Parks : [];
        },

        /**
         * Quotes albums by splitting them into Sivex-compatible blocks.
         *
         * @param {Object} params Quote parameters.
         * @param {Array<Object>} params.selectedParks Albums to quote.
         * @param {Object} params.baseItem Base cart item used to build each request.
         * @param {number} params.chunkSize Maximum block size for each quote request.
         * @returns {Promise<Object>} Consolidated quote result.
         */
        async QuoteParks({ selectedParks, baseItem, chunkSize = 0 }) {
            const safeChunkSize = Math.max(
                Number(chunkSize || selectedParks.length || 1),
                1
            );
            const chunkList = helper.methods.chunk(selectedParks, safeChunkSize);

            const tasks = chunkList.map((chunk) =>
                cartByRoomMix.methods.BuildChunkRequest(chunk, {
                    ...baseItem,
                    cantidad: chunk.length,
                    ServiceNumber: chunk.length,
                    Parks: chunk,
                })
            );

            const results = await Promise.all(tasks);

            const priceList = results.map((result) => result.price);
            const sivexPayData = results.map((result) => result.sivexData);

            const amount =
                priceList?.reduce((acc, { Amount }) => {
                    return acc + Number(Amount || 0);
                }, 0) || 0;

            return {
                amount,
                priceList,
                sivexPayData,
                priceSivex: {
                    ...baseItem,
                    Amount: amount,
                    NormalIndividual:
                        selectedParks.length > 0 ? amount / selectedParks.length : 0,
                    GrossAmount: amount,
                    Parks: selectedParks,
                    cantidad: selectedParks.length,
                    ServiceNumber: selectedParks.length,
                    sivexPayData,
                },
            };
        },

        /**
         * Rebuilds promotion flags for all currently added albums after a removal.
         * The albums are sorted by their selection order, split by maxAlbumsPerBatch,
         * and each block marks the first requiredPurchases albums as paid while the
         * remaining albums are marked as included.
         *
         * @param {Object} params Normalization parameters.
         * @param {Object} params.album Parent album object.
         * @param {Object} params.promotion Normalized promotion configuration.
         * @param {boolean} params.excludeUnlocked Whether already-unlocked albums must be ignored.
         * @returns {{addedParks: Array<Object>, paidParks: Array<Object>, includedParks: Array<Object>}} Normalized groups.
         */
        NormalizeManualPromotionSelections({
            album,
            promotion,
            excludeUnlocked = false,
        }) {
            const addedParks = this.SortPromotionParks(
                (album?.park || []).filter((park) => {
                    return (
                        park.IsAdded &&
                        this.IsRegularPhotoPark(park, { excludeUnlocked })
                    );
                })
            );

            const paidParks = [];
            const includedParks = [];
            const requiredPurchases = Number(promotion?.requiredPurchases || 0);
            const maxAlbumsPerBatch = Number(promotion?.maxAlbumsPerBatch || 0);
            const maxFreeAlbums = Number(promotion?.maxFreeAlbums || 0);

            this.ClearManualIncludeCandidates(album);

            if (
                !this.HasPromotionConfig(promotion || {}) ||
                !requiredPurchases ||
                !maxAlbumsPerBatch
            ) {
                addedParks.forEach((park) => {
                    this.MarkParkAsPaidSelection(park, album);
                    paidParks.push(park);
                });

                return {
                    addedParks,
                    paidParks,
                    includedParks,
                };
            }

            let includedAlbumsCount = 0;

            for (let index = 0; index < addedParks.length; index += maxAlbumsPerBatch) {
                const blockParks = addedParks.slice(index, index + maxAlbumsPerBatch);
                const shouldSplitBlock = blockParks.length > requiredPurchases;

                blockParks.forEach((park, blockIndex) => {
                    const canBeIncludedByPosition = shouldSplitBlock && blockIndex >= requiredPurchases;
                    const canBeIncludedByLimit = !maxFreeAlbums || includedAlbumsCount < maxFreeAlbums;
                    const shouldMarkAsIncluded = canBeIncludedByPosition && canBeIncludedByLimit;

                    if (shouldMarkAsIncluded) {
                        this.MarkParkAsIncludedSelection(park, album);
                        includedParks.push(park);
                        includedAlbumsCount++;
                        return;
                    }

                    this.MarkParkAsPaidSelection(park, album);
                    paidParks.push(park);
                });
            }

            return {
                addedParks,
                paidParks,
                includedParks,
            };
        },

        /**
         * Builds visual groups for the cart and payment views.
         * Groups are based on Sivex package data when available. Each commercial
         * block uses maxAlbumsPerBatch as its block size and requiredPurchases as
         * the number of albums that must show price. The remaining albums in the
         * block are displayed as included albums.
         *
         * @param {Object} params Grouping parameters.
         * @param {Array<Object>} params.displayParks Albums shown in the cart.
         * @param {Array<Object>} params.paidParks Albums that are part of the charge.
         * @param {Array<Object>} params.sivexPayData Sivex package responses.
         * @param {Object|null} params.promotion Normalized promotion configuration.
         * @returns {Array<Object>} Visual groups with paid, included, subtotal, and unit-price data.
         */
        BuildDisplayGroups({
            displayParks = [],
            paidParks = [],
            sivexPayData = [],
            promotion = null,
        }) {
            const orderedDisplayParks = this.SortPromotionParks(displayParks);
            const paidParkIds = paidParks.map((park) => Number(park.Park.Id));
            const usedParkIds = [];

            const requiredPurchases = Number(promotion?.requiredPurchases || 0);
            const maxAlbumsPerBatch = Number(promotion?.maxAlbumsPerBatch || 0);

            const shouldApplyVisualPromotionSplit =
                this.HasPromotionConfig(promotion || {}) &&
                requiredPurchases > 0 &&
                maxAlbumsPerBatch > 0;

            /**
             * Splits a visual group into paid albums and included albums.
             *
             * @param {Object} params Visual group parameters.
             * @param {Array<Object>} params.groupParks Albums included in the group.
             * @returns {{groupPaidParks: Array<Object>, groupIncludedParks: Array<Object>}} Split group.
             */
            const BuildVisualGroupParks = ({ groupParks }) => {
                const shouldSplitThisGroup =
                    shouldApplyVisualPromotionSplit &&
                    groupParks.length > requiredPurchases;

                if (!shouldSplitThisGroup) {
                    const groupPaidParks = groupParks.filter((park) =>
                        paidParkIds.includes(Number(park.Park.Id))
                    );

                    const groupIncludedParks = groupParks.filter(
                        (park) => !paidParkIds.includes(Number(park.Park.Id))
                    );

                    return {
                        groupPaidParks,
                        groupIncludedParks,
                    };
                }

                const visualPaidParkIds = groupParks
                    .filter((park) => paidParkIds.includes(Number(park.Park.Id)))
                    .slice(0, requiredPurchases)
                    .map((park) => Number(park.Park.Id));

                const groupPaidParks = groupParks.filter((park) =>
                    visualPaidParkIds.includes(Number(park.Park.Id))
                );

                const groupIncludedParks = groupParks.filter((park) => {
                    return !visualPaidParkIds.includes(Number(park.Park.Id));
                });

                return {
                    groupPaidParks,
                    groupIncludedParks,
                };
            };

            const groups = sivexPayData
                .map((pack, index) => {
                    const packParkIds = Array.isArray(pack.availableParks)
                        ? pack.availableParks.map((id) => Number(id))
                        : [];

                    const groupParks = orderedDisplayParks.filter((park) => {
                        const parkId = Number(park.Park.Id);
                        return packParkIds.includes(parkId);
                    });

                    if (!groupParks.length) {
                        return null;
                    }

                    groupParks.forEach((park) => {
                        usedParkIds.push(Number(park.Park.Id));
                    });

                    const { groupPaidParks, groupIncludedParks } =
                        BuildVisualGroupParks({
                            groupParks,
                        });

                    const subtotal = Number(pack.Amount || 0);

                    return {
                        groupNumber: index + 1,
                        parks: groupParks,
                        paidParks: groupPaidParks,
                        includedParks: groupIncludedParks,
                        subtotal,
                        paidUnitPrice:
                            groupPaidParks.length > 0
                                ? subtotal / groupPaidParks.length
                                : subtotal,
                        sivexPayData: pack,
                    };
                })
                .filter(Boolean);

            const orphanParks = orderedDisplayParks.filter(
                (park) => !usedParkIds.includes(Number(park.Park.Id))
            );

            if (orphanParks.length) {
                const { groupPaidParks, groupIncludedParks } = BuildVisualGroupParks({
                    groupParks: orphanParks,
                });

                groups.push({
                    groupNumber: groups.length + 1,
                    parks: orphanParks,
                    paidParks: groupPaidParks,
                    includedParks: groupIncludedParks,
                    subtotal: 0,
                    paidUnitPrice: 0,
                    sivexPayData: null,
                });
            }

            return groups;
        },

        /**
         * Builds the final cart item when a promotion is applied.
         *
         * @param {Object} params Cart item parameters.
         * @returns {Object} Cart item with promotion and pricing metadata.
         */
        BuildPromotionCartItem({
            quotedAll,
            paidParks,
            displayParks,
            promotion,
            promotionMode = "none",
            allAvailableParksLength = 0,
        }) {
            const paidParksList = Array.isArray(paidParks) ? paidParks : [];
            const displayParksList = Array.isArray(displayParks)
                ? displayParks
                : paidParksList;
            const finalQuote = quotedAll?.priceSivex || {};
            const sivexPayData = Array.isArray(quotedAll?.sivexPayData)
                ? quotedAll.sivexPayData
                : [];
            const amount = Number(quotedAll?.amount || finalQuote?.Amount || 0);
            const displayGroups = this.BuildDisplayGroups({
                displayParks: displayParksList,
                paidParks: paidParksList,
                sivexPayData,
                promotion,
            });
            const includedParksList = this.GetDisplayIncludedParks(
                displayParksList,
                paidParksList
            );
            const isManualPending = this.IsPromotionManualPending({
                promotion,
                paidParks: paidParksList,
                includedParks: includedParksList,
                availableParksLength: allAvailableParksLength,
            });
            const promotionPendingRemaining = isManualPending
                ? this.GetManualPromotionPendingRemaining({
                    promotion,
                    paidParks: paidParksList,
                    includedParks: includedParksList,
                    availableParksLength: allAvailableParksLength,
                })
                : 0;

            return {
                ...finalQuote,
                Amount: amount,
                NormalIndividual:
                    paidParksList.length > 0 ? amount / paidParksList.length : 0,
                GrossAmount: amount,
                Parks: displayParksList,
                PaidParks: paidParksList,
                PriceCalculationParks: displayParksList,
                DisplayGroups: displayGroups,
                sivexPayData,
                sivexPayDataPaid: sivexPayData,
                promotionSivexPayData: [],
                cantidad: displayParksList.length,
                ServiceNumber: displayParksList.length,
                PromotionApplied: promotionMode !== "none",
                PromotionMode: promotionMode,
                PromotionPaidQuantity: paidParksList.length,
                PromotionIncludedQuantity: includedParksList.length,
                IncludedAlbumsQuantity: includedParksList.length,
                PromotionRequiredPurchases: Number(promotion?.requiredPurchases || 0),
                PromotionMaxFreeAlbums: Number(promotion?.maxFreeAlbums || 0),
                PromotionMaxAlbumsPerBatch: Number(promotion?.maxAlbumsPerBatch || 0),
                PromotionAutoUnlockEnabled: promotion?.autoUnlockEnabled === true,
                PromotionAutoUnlockThreshold: Number(promotion?.autoUnlockThreshold || 0),
                HasAutoUnlockedParks: displayParksList.length > paidParksList.length,
                IsPromotionManualPending: isManualPending,
                PromotionPendingRemaining: promotionPendingRemaining,
                PromotionAvailableAlbumsQuantity: Number(allAvailableParksLength || displayParksList.length),
            };
        },

        /**
         * Gets display albums that are not part of the paid list.
         *
         * @param {Array<Object>} displayParks Albums shown in cart/payment.
         * @param {Array<Object>} paidParks Albums included in the charge.
         * @returns {Array<Object>} Albums displayed as included.
         */
        GetDisplayIncludedParks(displayParks = [], paidParks = []) {
            const paidParkIds = paidParks.map((park) => Number(park.Park.Id));

            return displayParks.filter(
                (park) => !paidParkIds.includes(Number(park.Park.Id))
            );
        },

        /**
         * Builds a regular cart item without an active promotion.
         *
         * @param {Object} params Regular cart item parameters.
         * @param {Object} params.quoted Sivex quote result.
         * @param {Array<Object>} params.paidParks Selected paid albums.
         * @returns {Object} Cart item without active promotion metadata.
         */
        BuildRegularCartItem({ quoted, paidParks }) {
            const paidParksList = Array.isArray(paidParks) ? paidParks : [];
            const paidQuote = quoted?.priceSivex || {};
            const amount = Number(paidQuote.Amount || quoted?.amount || 0);
            const sivexPayData = Array.isArray(quoted?.sivexPayData)
                ? quoted.sivexPayData
                : [];

            return {
                ...paidQuote,
                Amount: amount,
                NormalIndividual:
                    paidParksList.length > 0 ? amount / paidParksList.length : 0,
                GrossAmount: amount,
                Parks: paidParksList,
                PaidParks: paidParksList,
                PriceCalculationParks: paidParksList,
                DisplayGroups: this.BuildDisplayGroups({
                    displayParks: paidParksList,
                    paidParks: paidParksList,
                    sivexPayData,
                }),
                sivexPayData,
                sivexPayDataPaid: sivexPayData,
                promotionSivexPayData: [],
                cantidad: paidParksList.length,
                ServiceNumber: paidParksList.length,
                PromotionApplied: false,
                PromotionMode: "none",
                PromotionPaidQuantity: paidParksList.length,
                PromotionIncludedQuantity: 0,
                IncludedAlbumsQuantity: 0,
                HasAutoUnlockedParks: false,
                IsPromotionManualPending: false,
                PromotionPendingRemaining: 0,
            };
        },

        /**
         * Checks whether a list contains at least one added regular park album.
         *
         * @param {Array<Object>} parks Park/photo albums to evaluate.
         * @returns {boolean} True when at least one regular park album is selected.
         */
        HasSelectedRegularPark(parks = []) {
            return parks.some((park) => {
                return park.IsAdded && this.IsRegularPhotoPark(park);
            });
        },

        /**
         * Checks whether a download-code list contains at least one added regular park album.
         *
         * @param {Array<Object>} parks Park/photo albums to evaluate.
         * @returns {boolean} True when at least one new regular park album is selected.
         */
        HasSelectedRegularDownloadCodePark(parks = []) {
            return parks.some((park) => {
                return (
                    park.IsAdded &&
                    this.IsRegularPhotoPark(park, { excludeUnlocked: true })
                );
            });
        },

        /**
         * Finds the cart key that belongs to a QuotingService item.
         *
         * @param {Object} params Search parameters.
         * @param {Object} params.shoppingCart Current shopping cart indexed by dynamic keys.
         * @param {Object} params.park Park/photo album associated with the quotation service.
         * @param {string|number} params.tag UI flow identifier.
         * @returns {string|undefined} Matching cart key, or undefined when no match exists.
         */
        FindQuotingServiceCartKey({ shoppingCart, park, tag }) {
            const keys = Object.keys(shoppingCart || {});
            const tagValue = tag?.toString();
            const parkUuid = park?.uuid?.toString();
            const albumKeyId = park?.AlbumKey?.Id?.toString();

            const exactKey = keys.find((key) => {
                const item = shoppingCart[key];

                return (
                    key.toString() === tagValue ||
                    key.toString() === parkUuid ||
                    item?.uuid?.toString() === tagValue ||
                    item?.uuid?.toString() === parkUuid ||
                    (albumKeyId &&
                        item?.park?.AlbumKey?.Id?.toString() === albumKeyId)
                );
            });

            if (exactKey !== undefined) {
                return exactKey;
            }

            const parkId = park?.Park?.Id?.toString();
            const serviceId = park?.QuotingService?.Default?.toString();
            const visitDate = park?.VisitDate?.toString();

            return keys.find((key) => {
                const item = shoppingCart[key];
                const itemVisitDate = (item?.VisitDate || item?.date)?.toString();

                return (
                    item?.ParkId?.toString() === parkId &&
                    (!serviceId || item?.ServiceId?.toString() === serviceId) &&
                    (!visitDate || itemVisitDate === visitDate)
                );
            });
        },
    },

    /**
     * Lifecycle hook reserved for future mixin initialization.
     * It currently runs no logic to avoid side effects when components mount.
     *
     * @returns {void}
     */
    mounted() {},
};